import { useMemo, useState } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DataGridProps, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useInvoicesQuery } from '../../../../api/gql';
import { InvoiceStateBadge } from '../../../../components/invoice/InvoiceStateBadge';
import { ScheduledActions } from '../../../../components/invoice/ScheduledActions';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';
import { InvoiceArchiveButton } from '../../../../modules/invoice/InvoiceArchiveButton';
import { getPriceWithCurrencyString } from '../../../../utils/currencies';
import { getInvoiceStateProperties, getPlanTransKey, getTheOldestScheduledAction } from '../../../../utils/invoice';
import { notify } from '../../../../utils/notification';
import { fromCentesimal } from '../../../../utils/transform';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';

type Props = { isOverview?: boolean; id?: string } & Partial<DataGridProps>;

export const InvoiceTable = ({ isOverview, id = 'invoices', ...rest }: Props) => {
  const { t } = useTranslation();
  const { safeNavigate } = useSafeNavigation();
  const { isLoading, data, refetch } = useInvoicesQuery();

  const rows = useMemo(
    () =>
      data?.invoices
        .sort((a, b) => {
          if (a.id < b.id) return 1;
          return -1;
        })
        .map((invoice) => {
          const oldestScheduledAction = getTheOldestScheduledAction(invoice.scheduledActions);

          return {
            id: invoice.id,
            number: invoice.number,
            customer: invoice.customer?.name,
            vendor: invoice.vendor?.name,
            amountDue:
              invoice.amountDue != null
                ? getPriceWithCurrencyString(fromCentesimal(invoice.amountDue), invoice.currency)
                : '',
            createdAt: oldestScheduledAction ? oldestScheduledAction.scheduledAt : '',
            plan: invoice.plan ? t(getPlanTransKey(invoice.plan)) : '',
            state: invoice.status,
            checkedOut: invoice.scheduledActions,
            batchId: invoice.batchUpload?.id && !invoice.batchUpload?.planPaid ? invoice.batchUpload.id : '',
            batchType: invoice.batchUpload?.type,
            isAnnotated: invoice.isAnnotated,
            canBeArchived: invoice.canBeArchived,
          };
        }) ?? [],
    [data?.invoices, t],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'number',
        headerName: t('invoice.number') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'customer',
        headerName: t('customer') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'vendor',
        headerName: t('vendor') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'amountDue',
        headerName: t('amountRemaining') as string,
        flex: 1,
        minWidth: 100,
        align: 'right',
        renderCell: renderCellOrDefault,
      },
      {
        field: 'createdAt',
        headerName: t('remindingStart') as string,
        flex: 1,
        minWidth: 100,
        ...dateGridColDef,
      },
      {
        field: 'plan',
        headerName: t('planName') as string,
        flex: 1,
        minWidth: 100,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'state',
        headerName: t('status') as string,
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => {
          // transform value to enable filtering by its translation;
          if (!params.value) {
            return params.value;
          }
          const transKey = getInvoiceStateProperties(params.value, '')?.transKey;
          return t(transKey);
        },
        renderCell: (params) => {
          return <InvoiceStateBadge state={params.row.state} />;
        },
      },
      {
        field: 'checkedOut',
        headerName: t('checkedOut') as string,
        flex: 1,
        minWidth: 220,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => <ScheduledActions scheduledActions={params.value} />,
      },
      {
        field: 'action',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 150,
        renderCell: (params) => {
          const route = getInvoiceStateProperties(
            params.row.state,
            `${params.id}`,
            params.row.batchId,
            params.row.batchType,
            params.row.isAnnotated,
          ).route;
          return (
            <Button
              // when there is not checkbox selection, whole row is clickable and we prevent double routing
              onClick={(e) => (!rest?.checkboxSelection ? e.preventDefault() : null)}
              component={Link}
              to={route}
              endIcon={<ArrowRightAlt />}
            >
              {t('invoiceDetail')}
            </Button>
          );
        },
      },
    ],
    [t],
  );

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const onArchived = (failed?: string[]) => {
    refetch();

    if (failed?.length) {
      notify.negative({ message: t('archive.failed', { ns: 'translation' }) });
      return;
    }

    notify.positive({ message: t('archive.success', { ns: 'translation' }) });
  };
  const onArchiveError = () => {
    notify.negative({ message: t('archive.failed', { ns: 'translation' }) });
  };

  return (
    <Table
      {...rest}
      isOverviewMode={isOverview}
      id={id}
      loading={isLoading}
      autoHeight={true}
      rows={rows}
      columns={columns}
      checkboxSelection={rest?.checkboxSelection}
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      isRowSelectable={(params) => params.row.canBeArchived}
      rowSelectionModel={rowSelectionModel}
      toolbarContent={
        rowSelectionModel?.length ? (
          <Box display={'flex'} flex={1} justifyContent={'flex-end'}>
            <InvoiceArchiveButton
              invoiceIds={rowSelectionModel as string[]}
              onArchived={onArchived}
              onError={onArchiveError}
              variant={'text'}
              color={'error'}
            />
          </Box>
        ) : null
      }
      onRowClick={(rowParams) => {
        if (rest?.checkboxSelection) {
          return;
        }
        const route = getInvoiceStateProperties(
          rowParams.row.state,
          `${rowParams.id}`,
          rowParams.row.batchId,
          rowParams.row.batchType,
          rowParams.row.isAnnotated,
        ).route;
        safeNavigate(route);
      }}
    />
  );
};
